import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdAdd, MdClose, MdEdit, MdManageAccounts, MdSearch } from 'react-icons/md';
import { authenticatedFetch } from '../../lib/api';
import { useToast } from '../../components/shared/Toast';
import {
  ClearFiltersButton,
  erpPageBreadcrumbClass,
  erpPageBreadcrumbCurrentClass,
  erpPageBreadcrumbItemClass,
  erpPageBreadcrumbSepClass,
  TableBodySkeleton,
} from '@euroasiann/shared/ui';

type SocietyStatus = 'active' | 'inactive';
type IacsStatus = 'IACS' | 'Non-IACS';

interface ClassificationSociety {
  _id: string;
  name: string;
  shortCode: string;
  headquarters: string;
  country: string;
  email: string;
  iacsStatus: IacsStatus;
  status: SocietyStatus;
}

interface SocietyFormData {
  name: string;
  shortCode: string;
  headquarters: string;
  country: string;
  email: string;
  iacsStatus: IacsStatus;
  status: SocietyStatus;
}

const INITIAL_FORM: SocietyFormData = {
  name: '',
  shortCode: '',
  headquarters: '',
  country: '',
  email: '',
  iacsStatus: 'IACS',
  status: 'active',
};

function parseListPayload(payload: any): ClassificationSociety[] {
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
}

export function ClassificationSocietiesPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [societies, setSocieties] = useState<ClassificationSociety[]>([]);
  const [statusTab, setStatusTab] = useState<SocietyStatus>('active');
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingSociety, setEditingSociety] = useState<ClassificationSociety | null>(null);
  const [formData, setFormData] = useState<SocietyFormData>(INITIAL_FORM);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;

  const filteredSocieties = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return societies;
    return societies.filter(society => {
      const haystack = [society.name, society.shortCode, society.country, society.email]
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [societies, searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusTab]);

  const totalPages = Math.max(1, Math.ceil(filteredSocieties.length / PAGE_SIZE));
  const paginatedSocieties = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredSocieties.slice(start, start + PAGE_SIZE);
  }, [filteredSocieties, currentPage]);

  useEffect(() => {
    void fetchSocieties();
  }, [statusTab]);

  const fetchSocieties = async () => {
    setLoading(true);
    try {
      const response = await authenticatedFetch(
        `/api/v1/admin/classification-societies?status=${statusTab}&limit=200`
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to fetch classification societies');
      }
      const payload = await response.json();
      setSocieties(parseListPayload(payload));
    } catch (error: any) {
      showToast(error.message || 'Failed to fetch classification societies', 'error');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingSociety(null);
    setFormData(INITIAL_FORM);
    setIsModalOpen(true);
  };

  const openEditModal = (society: ClassificationSociety) => {
    setEditingSociety(society);
    setFormData({
      name: society.name,
      shortCode: society.shortCode,
      headquarters: society.headquarters,
      country: society.country,
      email: society.email,
      iacsStatus: society.iacsStatus,
      status: society.status,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingSociety(null);
    setFormData(INITIAL_FORM);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.shortCode.trim() || !formData.email.trim()) {
      showToast('Name, short code, and email are required', 'error');
      return;
    }
    if (!formData.headquarters.trim() || !formData.country.trim()) {
      showToast('Headquarters and country are required', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const endpoint = editingSociety
        ? `/api/v1/admin/classification-societies/${editingSociety._id}`
        : '/api/v1/admin/classification-societies';

      const response = await authenticatedFetch(endpoint, {
        method: editingSociety ? 'PUT' : 'POST',
        body: JSON.stringify({
          ...formData,
          shortCode: formData.shortCode.toUpperCase(),
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to save classification society');
      }

      showToast(
        editingSociety
          ? 'Classification society updated successfully'
          : 'Classification society created successfully',
        'success'
      );
      closeModal();
      await fetchSocieties();
    } catch (error: any) {
      showToast(error.message || 'Failed to save classification society', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeactivate = async (society: ClassificationSociety) => {
    if (!window.confirm(`Set "${society.shortCode}" to inactive?`)) return;

    try {
      const response = await authenticatedFetch(
        `/api/v1/admin/classification-societies/${society._id}`,
        { method: 'DELETE' }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to deactivate society');
      }
      showToast('Classification society set to inactive', 'success');
      await fetchSocieties();
    } catch (error: any) {
      showToast(error.message || 'Failed to deactivate society', 'error');
    }
  };

  return (
    <div className="w-full min-h-screen p-8">
      <div className="mb-6">
        <nav className={`${erpPageBreadcrumbClass} mb-2`} aria-label="Breadcrumb">
          <span className={erpPageBreadcrumbItemClass}>Admin</span>
          <span className={erpPageBreadcrumbSepClass}>/</span>
          <span className={erpPageBreadcrumbCurrentClass}>Classification</span>
        </nav>
        <h1 className="text-3xl font-bold text-[hsl(var(--foreground))]">Classification Societies</h1>
      </div>

      <div className="mb-6 flex gap-2 border-b border-[hsl(var(--border))]">
        <button
          onClick={() => setStatusTab('active')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            statusTab === 'active'
              ? 'border-[#0088cc] text-[hsl(var(--primary))]'
              : 'border-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]'
          }`}
        >
          Active
        </button>
        <button
          onClick={() => setStatusTab('inactive')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            statusTab === 'inactive'
              ? 'border-[#0088cc] text-[hsl(var(--primary))]'
              : 'border-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]'
          }`}
        >
          Inactive
        </button>
      </div>

      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 max-w-xl flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0088cc]/65 w-5 h-5 pointer-events-none" />
            <input
              type="text"
              placeholder="Search by name, short code, country, or email..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-transparent"
            />
          </div>
          <ClearFiltersButton
            visible={!!searchQuery.trim()}
            onClick={() => setSearchQuery('')}
            variant="toolbar"
          />
        </div>
        <button
          type="button"
          onClick={openCreateModal}
          className="inline-flex shrink-0 items-center justify-center gap-2 px-4 py-2.5 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
        >
          <MdAdd className="w-5 h-5" />
          Add Society
        </button>
      </div>

      <div className="bg-[hsl(var(--card))] rounded-lg border border-[hsl(var(--border))] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[hsl(var(--secondary))]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Short Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider">IACS Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Country</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider min-w-[220px]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-[hsl(var(--card))] divide-y divide-[#0088cc]/20 dark:divide-gray-800">
              {loading ? (
                <TableBodySkeleton rows={6} cols={7} />
              ) : filteredSocieties.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-[hsl(var(--muted-foreground))]">
                    {searchQuery.trim()
                      ? 'No societies match your search.'
                      : 'No classification societies found.'}
                  </td>
                </tr>
              ) : (
                paginatedSocieties.map(society => (
                  <tr key={society._id} className="hover:bg-[hsl(var(--secondary))]/40">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-[hsl(var(--foreground))]">{society.shortCode}</td>
                    <td className="px-6 py-4 text-sm text-[hsl(var(--foreground))]">{society.name}</td>
                    <td className="px-6 py-4 text-sm text-[hsl(var(--muted-foreground))]">{society.iacsStatus}</td>
                    <td className="px-6 py-4 text-sm text-[hsl(var(--muted-foreground))]">{society.country}</td>
                    <td className="px-6 py-4 text-sm text-[hsl(var(--muted-foreground))]">{society.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        society.status === 'active'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {society.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            navigate(
                              `/dashboard/admin/classification-societies/${society._id}/surveyors`,
                              { state: { society } }
                            )
                          }
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-[hsl(var(--primary))]/25 text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 hover:bg-[hsl(var(--primary))]/20 transition-colors"
                          title="Manage Surveyors"
                        >
                          <MdManageAccounts className="w-4 h-4 shrink-0" />
                          Surveyors
                        </button>
                        <button
                          type="button"
                          onClick={() => openEditModal(society)}
                          className="p-2 rounded-lg bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/20 hover:bg-[hsl(var(--primary))]/20 transition-colors"
                          title="Edit"
                          aria-label="Edit society"
                        >
                          <MdEdit className="w-4 h-4" />
                        </button>
                        {society.status === 'active' && (
                          <button
                            type="button"
                            onClick={() => handleDeactivate(society)}
                            className="p-2 rounded-lg bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] border border-[hsl(var(--destructive))]/20 hover:bg-[hsl(var(--destructive))]/20 transition-colors"
                            title="Deactivate"
                            aria-label="Deactivate society"
                          >
                            <MdClose className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {filteredSocieties.length > 0 && (
          <div className="flex items-center justify-between gap-4 px-6 py-4 border-t border-[hsl(var(--border))] bg-[hsl(var(--card))]">
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Page {currentPage} of {totalPages} · {filteredSocieties.length} total
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                className="px-3 py-2 rounded-lg text-sm font-medium border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[hsl(var(--secondary))] transition-colors"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                className="px-3 py-2 rounded-lg text-sm font-medium border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[hsl(var(--secondary))] transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md">
          <div className="bg-[hsl(var(--card))] rounded-lg shadow-xl w-full max-w-2xl mx-4 border border-[hsl(var(--border))]">
            <div className="flex items-center justify-between p-6 border-b border-[hsl(var(--border))]">
              <h2 className="text-xl font-bold text-[hsl(var(--foreground))]">
                {editingSociety ? 'Edit Classification Society' : 'Add Classification Society'}
              </h2>
              <button onClick={closeModal} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] dark:hover:text-gray-200">
                <MdClose className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--card))]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Short Code</label>
                <input
                  type="text"
                  value={formData.shortCode}
                  onChange={(e) => setFormData((prev) => ({ ...prev, shortCode: e.target.value.toUpperCase() }))}
                  className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--card))]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Headquarters</label>
                <input
                  type="text"
                  value={formData.headquarters}
                  onChange={(e) => setFormData((prev) => ({ ...prev, headquarters: e.target.value }))}
                  className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--card))]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Country</label>
                <input
                  type="text"
                  value={formData.country}
                  onChange={(e) => setFormData((prev) => ({ ...prev, country: e.target.value }))}
                  className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--card))]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--card))]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">IACS Status</label>
                <select
                  value={formData.iacsStatus}
                  onChange={(e) => setFormData((prev) => ({ ...prev, iacsStatus: e.target.value as IacsStatus }))}
                  className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--card))]"
                >
                  <option value="IACS">IACS</option>
                  <option value="Non-IACS">Non-IACS</option>
                </select>
              </div>
              {editingSociety && (
                <div>
                  <label className="block text-sm font-medium mb-2">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value as SocietyStatus }))}
                    className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--card))]"
                  >
                    <option value="active">active</option>
                    <option value="inactive">inactive</option>
                  </select>
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 p-6 border-t border-[hsl(var(--border))]">
              <button
                onClick={closeModal}
                className="px-4 py-2 border border-[#0088cc] text-[hsl(var(--primary))] rounded-lg hover:bg-blue-50 dark:hover:bg-blue-950/20 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-4 py-2 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {isSubmitting ? 'Saving...' : editingSociety ? 'Update Society' : 'Add Society'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

